/**
 * A workflow job read as the steps it ran, not as the checkpoints it wrote.
 *
 * The worker writes a checkpoint for everything: the attempt being picked up,
 * the run being prepared, every log line, and each step finishing. Only the
 * last of those is a step an operator cares about, so the page shows one card
 * per step per attempt and keeps the rest in a collapsed technical timeline.
 *
 * Everything below is derived from what the job-detail read already returns,
 * and each derivation says where it comes from:
 *
 * - A step card comes from the checkpoint that recorded the step's inputs and
 *   outputs when it finished (`inputs.step` names it), or from the log line a
 *   failing step writes before it gives up.
 * - A log line belongs to the checkpoint its own write produced. Every log
 *   call writes exactly one progress checkpoint with nothing recorded either
 *   side, so the run log and those checkpoints line up one to one, counted
 *   from the newest (the log keeps only its most recent entries). That
 *   checkpoint says which attempt the line was written in. A `map_step` item
 *   writes one too, and then lines pair by time instead (`pairLogsWithWrites`).
 * - No checkpoint marks a step starting. A step can only start once every step
 *   it depends on has finished and its attempt has begun, so "started" is the
 *   latest of those, which is exact for steps that run one after another.
 */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { ImageX } from '@untitledui/icons';
import { Button } from '@ui/components/base/buttons/button';
import { Tooltip, TooltipTrigger } from '@ui/components/base/tooltip/tooltip';
import { Callout, Code, StatusBadge } from '@ui/console/primitives';
import { formatDuration } from '@ui/console/measures';
import { formatTimestamp, humanize } from '@ui/console/format';
import { useClipboard } from '@ui/hooks/use-clipboard';
import { cx } from '@ui/utils/cx';
import { adminFetch, apiBaseUrl, handleUnauthorized } from './auth';
import { GarmentImage } from './garment-image';
import { CopyableId, EntityRef, looksLikeUuid, refColumnLabel, refForField } from './references';
import { OutfitListView, stepOutfitLists } from './step-outfits';

type Row = Record<string, unknown>;

function record(value: unknown): Row | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Row) : undefined;
}

function text(row: Row | undefined, key: string): string | undefined {
  const value = row?.[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

function time(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : undefined;
}

/* ---------------------------------------------------------------- shapes */

export type StepCheckpoint = {
  sequence: number;
  attempt: number;
  checkpoint_type: string;
  created_at: string | undefined;
  inputs: unknown;
  outputs: unknown;
  output_references: Row | undefined;
};

export type StepLogEntry = {
  timestamp: string | undefined;
  message: string;
  step: string | undefined;
  fields: Row;
  error: string | undefined;
  errorType: string | undefined;
};

export type StepStatus = 'completed' | 'failed' | 'running' | 'interrupted';

export type StepCard = {
  key: string;
  step: string;
  /** The job attempt this ran in. A retried step has one card per attempt. */
  attempt: number;
  /** Tries inside the step's own retry loop, when it needed more than one. */
  tries: number | undefined;
  status: StepStatus;
  startedAt: string | undefined;
  finishedAt: string | undefined;
  upstream: string[];
  /** `null` is "not recorded"; the payload stands in when a step failed first. */
  inputs: unknown;
  /** Exactly what was recorded, for the raw toggle under the fields. */
  rawInputs: unknown;
  inputsFromPayload: boolean;
  outputs: unknown;
  outputsRecorded: boolean;
  references: Array<[string, string]>;
  logs: StepLogEntry[];
  sequence: number;
};

/** `MAX_RUN_LOG_ENTRIES` upstream: the run log keeps only its newest lines. */
export const MAX_RUN_LOG_ENTRIES = 200;

/** A full run log may have dropped older lines; a shorter one is complete. */
export function runLogCapped(logs: readonly unknown[]): boolean {
  return logs.length >= MAX_RUN_LOG_ENTRIES;
}

export type StepReading = {
  cards: StepCard[];
  /** The run log is at its cap, so older lines were dropped and are not here. */
  logsCapped: boolean;
  /** Log lines that belong to no step card, for the technical timeline. */
  unmatched: StepLogEntry[];
  /** What each progress checkpoint was, keyed by sequence, for the timeline. */
  progress: Map<
    number,
    { step: string | undefined; kind: 'finished' | 'logged'; message?: string }
  >;
};

const STEP_PROGRESS = 'workflow.step_progress';
const ATTEMPT_BOUNDARIES = new Set(['attempt.started', 'work.prepared']);
const STEP_META_KEYS = new Set(['step', 'attempt', 'payload', 'upstream_steps']);

function isStepFinish(checkpoint: StepCheckpoint): boolean {
  return (
    checkpoint.checkpoint_type === STEP_PROGRESS &&
    typeof record(checkpoint.inputs)?.['step'] === 'string'
  );
}

function isLogWrite(checkpoint: StepCheckpoint): boolean {
  return (
    checkpoint.checkpoint_type === STEP_PROGRESS &&
    (checkpoint.inputs === null || checkpoint.inputs === undefined) &&
    (checkpoint.outputs === null || checkpoint.outputs === undefined) &&
    !Object.keys(checkpoint.output_references ?? {}).length
  );
}

/** What a step read: the workflow payload plus anything else it recorded. */
function stepInputs(inputs: Row): Row {
  const payload = record(inputs['payload']) ?? {};
  const extra = Object.fromEntries(
    Object.entries(inputs).filter(([key]) => !STEP_META_KEYS.has(key)),
  );
  return { ...payload, ...extra };
}

function latest(values: Array<string | undefined>): string | undefined {
  let best: { at: number; value: string } | undefined;
  for (const value of values) {
    const at = time(value);
    if (value && at !== undefined && (!best || at > best.at)) best = { at, value };
  }
  return best?.value;
}

/** How far a worker's log clock may run ahead of the database's checkpoint clock. */
const CLOCK_SKEW_MS = 1_000;

/**
 * Pair each log line with the progress checkpoint its write produced.
 *
 * Normally every bare progress checkpoint is a log write, so the two line up
 * one to one counting from the newest (the log keeps only its most recent
 * entries, and a resumed job can carry lines seeded from an older one, which
 * have no checkpoint here). A `map_step` item also saves progress with nothing
 * recorded, so when there are more bare writes than lines the count no longer
 * lines up; then each line takes, in order, the first unused write made at or
 * after its own timestamp, which is enough to know its attempt.
 */
function pairLogsWithWrites(
  logs: readonly StepLogEntry[],
  writes: readonly StepCheckpoint[],
): Array<{ entry: StepLogEntry; write: StepCheckpoint | undefined }> {
  if (writes.length <= logs.length) {
    const offset = logs.length - writes.length;
    return logs.map((entry, index) => ({
      entry,
      write: index - offset >= 0 ? writes[index - offset] : undefined,
    }));
  }
  let next = 0;
  return logs.map((entry) => {
    const at = time(entry.timestamp);
    if (at === undefined) return { entry, write: undefined };
    while (next < writes.length && (time(writes[next]?.created_at) ?? 0) < at - CLOCK_SKEW_MS)
      next += 1;
    const write = writes[next];
    if (write) next += 1;
    return { entry, write };
  });
}

export function readSteps({
  checkpoints,
  logs,
  payload,
  jobStatus,
}: {
  checkpoints: readonly StepCheckpoint[];
  logs: readonly StepLogEntry[];
  payload: Row | undefined;
  jobStatus: string | undefined;
}): StepReading {
  const ordered = [...checkpoints].sort((left, right) => left.sequence - right.sequence);
  const progress: StepReading['progress'] = new Map();
  const cards = new Map<string, StepCard>();
  const keyOf = (step: string, attempt: number) => `${step}#${attempt}`;

  for (const checkpoint of ordered.filter(isStepFinish)) {
    const inputs = record(checkpoint.inputs) ?? {};
    const step = String(inputs['step']);
    const tries = typeof inputs['attempt'] === 'number' ? inputs['attempt'] : undefined;
    progress.set(checkpoint.sequence, { step, kind: 'finished' });
    cards.set(keyOf(step, checkpoint.attempt), {
      key: keyOf(step, checkpoint.attempt),
      step,
      attempt: checkpoint.attempt,
      tries: tries && tries > 1 ? tries : undefined,
      status: 'completed',
      startedAt: undefined,
      finishedAt: checkpoint.created_at,
      upstream: strings(inputs['upstream_steps']),
      inputs: stepInputs(inputs),
      rawInputs: checkpoint.inputs,
      inputsFromPayload: false,
      outputs: checkpoint.outputs,
      outputsRecorded: checkpoint.outputs !== null && checkpoint.outputs !== undefined,
      references: Object.entries(checkpoint.output_references ?? {}).filter(
        (entry): entry is [string, string] => typeof entry[1] === 'string',
      ),
      logs: [],
      sequence: checkpoint.sequence,
    });
  }

  const writes = ordered.filter(isLogWrite);
  const paired = pairLogsWithWrites(logs, writes);
  const lastAttempt = Math.max(0, ...ordered.map((checkpoint) => checkpoint.attempt));

  // A step that failed never wrote its finishing checkpoint; the line it logs
  // on the way out is its only trace, so that line makes its card.
  for (const { entry, write } of paired) {
    if (!write || !entry.step) continue;
    const key = keyOf(entry.step, write.attempt);
    if (cards.has(key)) continue;
    const failed = Boolean(entry.error || entry.errorType);
    // A step that logged and then neither finished nor failed is still
    // running, or its worker was lost mid-step. Either way it is a step.
    const stillGoing = jobStatus === 'running' && write.attempt === lastAttempt;
    cards.set(key, {
      key,
      step: entry.step,
      attempt: write.attempt,
      tries: undefined,
      status: failed ? 'failed' : stillGoing ? 'running' : 'interrupted',
      startedAt: undefined,
      finishedAt: failed ? entry.timestamp : undefined,
      upstream: [],
      inputs: payload ?? null,
      rawInputs: payload ?? null,
      inputsFromPayload: Boolean(payload),
      outputs: null,
      outputsRecorded: false,
      references: [],
      logs: [],
      sequence: write.sequence,
    });
  }

  const unmatched: StepLogEntry[] = [];
  for (const { entry, write } of paired) {
    if (write)
      progress.set(write.sequence, { step: entry.step, kind: 'logged', message: entry.message });
    const card = write && entry.step ? cards.get(keyOf(entry.step, write.attempt)) : undefined;
    if (!card) {
      unmatched.push(entry);
      continue;
    }
    card.logs.push(entry);
    if (entry.error || entry.errorType) {
      card.status = 'failed';
      card.finishedAt = card.finishedAt ?? entry.timestamp;
      card.sequence = Math.max(card.sequence, write?.sequence ?? card.sequence);
    }
  }

  // Started: the latest moment every prerequisite was in place.
  const finishes = ordered.filter(isStepFinish);
  for (const card of cards.values()) {
    const boundaries = ordered.filter(
      (checkpoint) =>
        ATTEMPT_BOUNDARIES.has(checkpoint.checkpoint_type) &&
        checkpoint.attempt === card.attempt &&
        checkpoint.sequence < card.sequence,
    );
    const before = finishes.filter(
      (checkpoint) =>
        checkpoint.sequence < card.sequence &&
        (card.status === 'completed'
          ? card.upstream.includes(String(record(checkpoint.inputs)?.['step']))
          : checkpoint.attempt === card.attempt),
    );
    card.startedAt = latest([...boundaries, ...before].map((checkpoint) => checkpoint.created_at));
  }

  return {
    cards: [...cards.values()].sort((left, right) => left.sequence - right.sequence),
    unmatched,
    progress,
    logsCapped: runLogCapped(logs),
  };
}

/* ---------------------------------------------------------- image refs */

const IMAGE_EXTENSION = /\.(png|jpe?g|webp)$/i;
const UUID = '[0-9a-fA-F-]{36}';
/** The reviewed logical asset namespaces, as the backend's registry names them. */
const LOGICAL_ASSET = new RegExp(
  [
    `garment:${UUID}:(thumb|full)`,
    `garment-variation:${UUID}`,
    `outfit:${UUID}:try-on(:thumb)?`,
    `shop-product:${UUID}:[0-9]+`,
    `order-item:${UUID}`,
    'studio-render:[A-Za-z0-9_-]{1,256}',
    `avatar-candidate:${UUID}`,
    'member:(avatar|portrait)',
  ]
    .map((pattern) => `^${pattern}$`)
    .join('|'),
);

/** A storage key, signed URL or logical asset id that names a picture. */
export function isImageRef(value: string): boolean {
  if (!value || value.length > 1000) return false;
  if (LOGICAL_ASSET.test(value)) return true;
  if (/^https?:\/\//i.test(value)) {
    try {
      return IMAGE_EXTENSION.test(new URL(value).pathname);
    } catch {
      return false;
    }
  }
  if (value.includes('://') || value.startsWith('data:') || /\s/.test(value)) return false;
  return IMAGE_EXTENSION.test(value.split(/[?#]/, 1)[0] ?? '');
}

/** Image refs anywhere inside a nested value, bounded like the backend walk. */
function nestedImageRefs(value: unknown, found: string[] = [], depth = 0): string[] {
  if (depth > 8 || found.length >= 16) return found;
  if (typeof value === 'string') {
    if (isImageRef(value) && !found.includes(value)) found.push(value);
  } else if (Array.isArray(value)) {
    for (const item of value) nestedImageRefs(item, found, depth + 1);
  } else if (record(value)) {
    for (const item of Object.values(value as Row)) nestedImageRefs(item, found, depth + 1);
  }
  return found;
}

const MEDIA_FAILURE: Record<number, string> = {
  404: 'Not found in storage',
  413: 'Too large to preview',
  415: 'Not an image',
};

/**
 * One of the job's own image refs, read through the operator media route.
 *
 * The route is bearer-gated, so the bytes are fetched here and shown as an
 * object URL through `GarmentImage`, the one component in admin that opens an
 * image element. A ref that cannot be loaded is a small labelled placeholder,
 * never a broken image.
 */
export function RefImage({ jobId, reference }: { jobId: string; reference: string }) {
  const [state, setState] = useState<
    { phase: 'loading' } | { phase: 'ready'; url: string } | { phase: 'failed'; reason: string }
  >({ phase: 'loading' });

  useEffect(() => {
    let live = true;
    let objectUrl: string | undefined;
    const controller = new AbortController();
    setState({ phase: 'loading' });
    void (async () => {
      try {
        const url = new URL(
          `${apiBaseUrl()}/api/v1/internal/operator/workflow-jobs/jobs/${jobId}/checkpoint-media`,
        );
        url.searchParams.set('reference', reference);
        const response = await adminFetch(url, { signal: controller.signal });
        handleUnauthorized(response.status);
        if (!response.ok) {
          if (live)
            setState({ phase: 'failed', reason: MEDIA_FAILURE[response.status] ?? 'Unavailable' });
          return;
        }
        const blob = await response.blob();
        if (!live) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ phase: 'ready', url: objectUrl });
      } catch {
        if (live) setState({ phase: 'failed', reason: 'Could not be reached' });
      }
    })();
    return () => {
      live = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [jobId, reference]);

  return (
    <figure className="m-0 flex w-32 min-w-0 flex-col gap-1" data-media-state={state.phase}>
      {state.phase === 'ready' ? (
        <a
          href={state.url}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open ${reference} full size`}
          className="block rounded-lg outline-brand focus-visible:outline-2"
        >
          <GarmentImage url={state.url} alt={reference} className="w-32" />
        </a>
      ) : state.phase === 'loading' ? (
        <div
          className="aspect-square w-32 animate-pulse rounded-lg border border-secondary bg-secondary"
          aria-label="Loading preview"
        />
      ) : (
        <div
          className="flex aspect-square w-32 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-secondary bg-secondary p-2 text-center"
          role="img"
          aria-label={`No preview for ${reference}: ${state.reason}`}
        >
          <ImageX className="size-5 text-fg-quaternary" />
          <span className="text-xs text-tertiary">{state.reason}</span>
        </div>
      )}
      <figcaption className="font-mono text-[11px] leading-tight break-all text-tertiary select-text">
        {reference}
      </figcaption>
    </figure>
  );
}

/* ---------------------------------------------------------- field values */

/** A long id reads whole, wraps rather than truncates, and copies on click. */
function CopyValue({ value }: { value: string }) {
  const { copied, copy } = useClipboard();
  return (
    <button
      type="button"
      onClick={() => void copy(value)}
      title="Click to copy"
      className="max-w-full cursor-copy rounded-md bg-secondary px-1.5 py-0.5 text-left font-mono text-xs break-all text-secondary outline-brand select-text focus-visible:outline-2"
    >
      {value}
      {copied ? <span className="ml-1.5 font-sans text-brand-secondary">Copied</span> : null}
    </button>
  );
}

function RawToggle({ value, label = 'raw' }: { value: unknown; label?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="flex min-w-0 flex-col items-start gap-1">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className="font-mono text-[11px] text-brand-secondary underline outline-brand focus-visible:outline-2"
      >
        {open ? `hide ${label}` : label}
      </button>
      {open ? (
        <pre className="max-h-64 w-full overflow-auto rounded-lg bg-secondary p-3 font-mono text-xs break-words whitespace-pre-wrap text-secondary select-text">
          {JSON.stringify(value, null, 2)}
        </pre>
      ) : null}
    </span>
  );
}

const ID_ISH = /(^id$|_id$|_ids$|_ref$|_refs$|_uuid$)/;

/** `garment_ids` names a list of what `garment_id` names one of. */
function singularKey(key: string): string {
  return key.replace(/_ids$/, '_id').replace(/_refs$/, '_ref');
}

/**
 * A string under an id-shaped key: the thing it names, by name, when the key
 * says what it is; a short copyable id when it is some other uuid. The raw
 * thirty-six characters are never the primary text.
 */
function identifiedValue(field: string, value: string, siblings: Row): ReactNode {
  const ref = refForField(singularKey(field), value, siblings);
  if (ref) return <EntityRef {...ref} />;
  if (looksLikeUuid(value)) return <CopyableId value={value} label={fieldLabel(field)} />;
  return undefined;
}

/** A field's heading in plain words: `run_id` reads "Run", `member_id` "Member". */
export function fieldLabel(key: string): string {
  const named = refColumnLabel(singularKey(key));
  if (named) return key.endsWith('_ids') ? `${named}s` : named;
  if (/_ids$/.test(key)) return humanize(key.replace(/_ids$/, 's'));
  if (/_id$/.test(key)) return humanize(key.replace(/_id$/, ''));
  return humanize(key);
}

function FieldValue({
  value,
  jobId,
  field,
  siblings = {},
}: {
  value: unknown;
  jobId: string;
  /** The key this value sits under, so an id can be read as what it names. */
  field?: string;
  siblings?: Row;
}): ReactNode {
  if (value === null || value === undefined) return <Code>null</Code>;
  if (typeof value === 'string') {
    if (isImageRef(value)) return <RefImage jobId={jobId} reference={value} />;
    if (field && ID_ISH.test(field)) {
      const identified = identifiedValue(field, value, siblings);
      if (identified !== undefined) return identified;
    }
    return <CopyValue value={value} />;
  }
  if (typeof value !== 'object') return <Code>{JSON.stringify(value)}</Code>;
  const list: readonly unknown[] | undefined = Array.isArray(value) ? value : undefined;
  if (list && list.every((item) => item === null || typeof item !== 'object')) {
    if (!list.length) return <span className="text-sm text-quaternary">Empty list</span>;
    return (
      <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
        {list.map((item, index) => (
          <li key={`${JSON.stringify(item)}-${index}`}>
            <FieldValue
              value={item}
              jobId={jobId}
              {...(field ? { field } : {})}
              siblings={siblings}
            />
          </li>
        ))}
      </ul>
    );
  }
  const size = Array.isArray(value) ? value.length : Object.keys(value).length;
  const noun = Array.isArray(value)
    ? size === 1
      ? 'entry'
      : 'entries'
    : size === 1
      ? 'field'
      : 'fields';
  const images = nestedImageRefs(value);
  return (
    <span className="flex min-w-0 flex-col gap-1.5">
      <span className="flex items-center gap-2 text-xs text-tertiary">
        {size} {noun}
      </span>
      {images.length ? (
        <span className="flex flex-wrap gap-2">
          {images.map((reference) => (
            <RefImage key={reference} jobId={jobId} reference={reference} />
          ))}
        </span>
      ) : null}
      <RawToggle value={value} />
    </span>
  );
}

/**
 * A step's inputs or outputs as key/value rows, every field visible at once.
 * `null` means the run never recorded it; `{}` means it did, and was empty.
 */
export function IoFields({ value, jobId }: { value: unknown; jobId: string }) {
  if (value === null || value === undefined)
    return <span className="text-sm text-quaternary">Not recorded for this run</span>;
  const fields = record(value);
  if (!fields) return <FieldValue value={value} jobId={jobId} />;
  const entries = Object.entries(fields);
  if (!entries.length) return <span className="text-sm text-secondary">Recorded, and empty</span>;
  return (
    <dl className="m-0 flex min-w-0 flex-col gap-2">
      {entries.map(([key, entry]) => (
        <div key={key} className="flex min-w-0 flex-col gap-0.5" data-field={key}>
          <dt className="text-xs text-tertiary" title={key}>
            {fieldLabel(key)}
          </dt>
          <dd className="m-0 min-w-0">
            <FieldValue value={entry} jobId={jobId} field={key} siblings={fields} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

/* ------------------------------------------------------------- step card */

function when(value: string | undefined): string {
  return value ? formatTimestamp(value) : 'Not recorded';
}

function durationOf(card: StepCard): string | undefined {
  const start = time(card.startedAt);
  const end = time(card.finishedAt);
  if (start === undefined || end === undefined || end < start) return undefined;
  return formatDuration((end - start) / 1000);
}

const LOG_HIDDEN_FIELDS = new Set(['error', 'error_type', 'error_code']);

export function LogLine({ entry }: { entry: StepLogEntry }) {
  const code = text(entry.fields, 'error_code');
  const failed = Boolean(entry.error || entry.errorType || code);
  const rest = Object.entries(entry.fields).filter(([key]) => !LOG_HIDDEN_FIELDS.has(key));
  const body = (
    <span className="flex min-w-0 flex-col gap-1">
      <span className="flex flex-wrap items-baseline gap-x-2">
        <span className={cx('text-sm font-medium', failed ? 'text-error-primary' : 'text-primary')}>
          {entry.message || 'Logged with no message'}
        </span>
        {entry.timestamp ? (
          <span className="text-xs text-tertiary">{formatTimestamp(entry.timestamp)}</span>
        ) : null}
      </span>
      {rest.length ? (
        <span className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-xs break-all text-tertiary">
          {rest.map(([key, value]) => (
            <span key={key}>
              {key}={typeof value === 'string' ? value : JSON.stringify(value)}
            </span>
          ))}
        </span>
      ) : null}
    </span>
  );
  if (!failed) return <li className="py-1.5">{body}</li>;
  return (
    <li className="py-1.5" data-log-state="failed">
      <Callout tone="error" title={entry.message || 'This step failed'}>
        <span className="flex flex-col gap-1">
          {entry.error || entry.errorType ? (
            <span className="font-medium">
              {entry.errorType ? `${entry.errorType}: ` : ''}
              {entry.error}
            </span>
          ) : null}
          {code ? <Code>{code}</Code> : null}
          {entry.timestamp ? (
            <span className="text-xs opacity-75">{formatTimestamp(entry.timestamp)}</span>
          ) : null}
          {rest.length ? (
            <span className="font-mono text-xs break-all opacity-75">
              {rest.map(([key, value]) => `${key}=${JSON.stringify(value)}`).join(' ')}
            </span>
          ) : null}
        </span>
      </Callout>
    </li>
  );
}

function Column({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="flex min-w-0 flex-col gap-2" data-column={title}>
      <h4 className="text-xs font-semibold tracking-wide text-tertiary uppercase">{title}</h4>
      {children}
    </section>
  );
}

export function StepCardView({
  card,
  jobId,
  logsCapped = false,
  siblings = [],
  memberId,
}: {
  card: StepCard;
  jobId: string;
  /** The run log is at its cap: this step's older lines may have been dropped. */
  logsCapped?: boolean;
  /** The job's other step cards, for a step whose output is read against an earlier one. */
  siblings?: readonly StepCard[];
  /** The member the job is about, to resolve garment images. */
  memberId?: string | undefined;
}) {
  const duration = durationOf(card);
  const failed = card.status === 'failed';
  // Outfit lists are drawn as spread cards below, so the field list leaves them out.
  const outfits = stepOutfitLists(card, siblings);
  const outputs = record(card.outputs);
  const outputFields =
    outfits.consumed.size && outputs
      ? Object.fromEntries(
          Object.entries(outputs).filter(([field]) => !outfits.consumed.has(field)),
        )
      : card.outputs;
  return (
    <li
      data-step={card.step}
      data-step-attempt={card.attempt}
      data-step-status={card.status}
      className={cx(
        'flex flex-col gap-4 rounded-xl border p-4',
        failed ? 'border-error-secondary bg-error-primary' : 'border-secondary bg-primary',
      )}
    >
      <header className="flex flex-col gap-2" data-part="header">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h3 className="font-mono text-sm font-semibold break-all text-primary">{card.step}</h3>
          <StatusBadge value={card.status} />
          <span className="text-xs text-tertiary">
            Attempt {card.attempt}
            {card.tries ? ` · succeeded on try ${card.tries}` : ''}
          </span>
          <span className="text-xs text-tertiary">Started {when(card.startedAt)}</span>
          <span className="text-xs text-tertiary">
            {duration
              ? `Took ${duration}`
              : card.status === 'running'
                ? 'Still running'
                : 'Duration not known'}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-tertiary">
          {card.upstream.length ? (
            <>
              <span>Ran after</span>
              {card.upstream.map((step) => (
                <span
                  key={step}
                  className="rounded-md bg-secondary px-1.5 py-0.5 font-mono text-[11px] text-secondary"
                >
                  {step}
                </span>
              ))}
            </>
          ) : card.status === 'completed' ? (
            <span>No earlier step was needed</span>
          ) : null}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-part="io">
        <Column title="Inputs">
          {card.inputsFromPayload ? (
            <span className="text-xs text-tertiary">
              The step stopped before recording its own inputs. This is what the job was given.
            </span>
          ) : null}
          <IoFields value={card.inputs} jobId={jobId} />
          {card.rawInputs !== null && card.rawInputs !== undefined ? (
            <RawToggle value={card.rawInputs} label="raw inputs" />
          ) : null}
        </Column>
        <Column title="Outputs">
          {card.outputsRecorded ? (
            <>
              {outfits.consumed.size && !Object.keys(record(outputFields) ?? {}).length ? (
                <span className="text-xs text-tertiary">The outfits are drawn below.</span>
              ) : (
                <IoFields value={outputFields} jobId={jobId} />
              )}
              <RawToggle value={card.outputs} label="raw outputs" />
            </>
          ) : (
            <span className="text-sm text-quaternary">
              {failed
                ? 'Nothing — the step failed before producing an output.'
                : card.status === 'running'
                  ? 'Not yet — the step is still running.'
                  : 'Not recorded for this run'}
            </span>
          )}
          {card.references.length ? (
            <div className="flex flex-wrap gap-3">
              {card.references.map(([name, reference]) => (
                <div key={name} className="flex flex-col gap-1">
                  <span className="text-xs text-tertiary">{humanize(name)}</span>
                  <RefImage jobId={jobId} reference={reference} />
                </div>
              ))}
            </div>
          ) : null}
        </Column>
      </div>

      {outfits.lists.length ? (
        <div className="flex flex-col gap-4" data-part="outfits">
          {outfits.lists.map((list) => (
            <OutfitListView key={list.field} list={list} memberId={memberId} />
          ))}
        </div>
      ) : null}

      <section aria-label="Logs" className="flex flex-col gap-1" data-part="logs">
        <h4 className="text-xs font-semibold tracking-wide text-tertiary uppercase">Logs</h4>
        {card.logs.length ? (
          <ul className="m-0 flex list-none flex-col divide-y divide-secondary p-0">
            {card.logs.map((entry, index) => (
              <LogLine key={`${entry.timestamp ?? index}-${index}`} entry={entry} />
            ))}
          </ul>
        ) : (
          <span className="text-sm text-quaternary">
            {logsCapped
              ? `None of the newest ${MAX_RUN_LOG_ENTRIES} log lines kept for this job came from this step.`
              : 'This step logged nothing.'}
          </span>
        )}
        {card.logs.length && logsCapped ? (
          <span className="text-xs text-quaternary">
            Only the newest {MAX_RUN_LOG_ENTRIES} log lines are kept for a job, so older lines from
            this step may be missing.
          </span>
        ) : null}
      </section>
    </li>
  );
}

/* ---------------------------------------------------- technical timeline */

const CHECKPOINT_WORDS: Record<string, (attempt: number) => string> = {
  'attempt.started': (attempt) => `Attempt ${attempt} picked up by a worker`,
  'work.prepared': () => 'Run prepared',
  'work.completed': () => 'Run finished its work',
  'attempt.completed': (attempt) => `Attempt ${attempt} ended`,
  'workflow.started': () => 'Workflow started',
  'workflow.completed': () => 'Workflow finished',
  'step.skipped': () => 'A step was skipped because an earlier run had already finished it',
};

/** A checkpoint in words. The raw name never leads; it sits in the tooltip. */
export function describeCheckpoint(
  checkpoint: StepCheckpoint,
  progress: StepReading['progress'],
): { label: string; raw: string } {
  const id = `#${checkpoint.sequence}`;
  if (checkpoint.checkpoint_type === STEP_PROGRESS) {
    const known = progress.get(checkpoint.sequence);
    const label =
      known?.kind === 'finished'
        ? `${known.step} finished`
        : known?.kind === 'logged'
          ? `${known.step ?? 'Workflow'} logged “${known.message ?? ''}”`
          : 'Progress saved';
    return { label, raw: id };
  }
  const words = CHECKPOINT_WORDS[checkpoint.checkpoint_type];
  if (words)
    return { label: words(checkpoint.attempt), raw: `${checkpoint.checkpoint_type} ${id}` };
  const bridged = checkpoint.checkpoint_type.startsWith('legacy_bridge.')
    ? `Progress: ${humanize(checkpoint.checkpoint_type.slice('legacy_bridge.'.length))}`
    : humanize(checkpoint.checkpoint_type.replace(/[.]/g, '_'));
  return { label: bridged, raw: `${checkpoint.checkpoint_type} ${id}` };
}

/** Said wherever the run log is shown while it is at its cap. */
export function LogCapNote() {
  return (
    <span className="text-xs text-quaternary" data-part="log-cap">
      Only the newest {MAX_RUN_LOG_ENTRIES} log lines are kept for a job. Older lines were dropped
      and are not shown.
    </span>
  );
}

export function TimelineLabel({ label, raw }: { label: string; raw: string }) {
  return (
    <Tooltip title={<span className="font-mono">{raw}</span>}>
      <TooltipTrigger className="cursor-help text-left text-sm text-primary underline decoration-dotted underline-offset-[3px]">
        {label}
      </TooltipTrigger>
    </Tooltip>
  );
}

export function TechnicalTimeline({
  checkpoints,
  reading,
}: {
  checkpoints: readonly StepCheckpoint[];
  reading: StepReading;
}) {
  const [open, setOpen] = useState(false);
  const ordered = [...checkpoints].sort((left, right) => left.sequence - right.sequence);
  return (
    <div className="flex flex-col gap-3" data-part="technical-timeline">
      <div>
        <Button
          size="sm"
          color="secondary"
          aria-expanded={open}
          aria-controls="workflow-technical-timeline"
          onClick={() => setOpen((current) => !current)}
        >
          {open ? 'Hide technical timeline' : 'Show technical timeline'}
        </Button>
      </div>
      {open ? (
        <div id="workflow-technical-timeline" className="flex flex-col gap-4">
          {ordered.length ? (
            <ol className="m-0 flex list-none flex-col gap-1 border-l-2 border-secondary p-0 pl-4">
              {ordered.map((checkpoint) => {
                const { label, raw } = describeCheckpoint(checkpoint, reading.progress);
                const carries =
                  !isStepFinish(checkpoint) &&
                  ((checkpoint.inputs !== null && checkpoint.inputs !== undefined) ||
                    (checkpoint.outputs !== null && checkpoint.outputs !== undefined) ||
                    Object.keys(checkpoint.output_references ?? {}).length > 0);
                return (
                  <li
                    key={checkpoint.sequence}
                    className="flex flex-col gap-0.5 py-1"
                    data-timeline-entry="checkpoint"
                  >
                    <span className="flex flex-wrap items-center gap-x-3">
                      <TimelineLabel label={label} raw={raw} />
                      <span className="text-xs text-tertiary">attempt {checkpoint.attempt}</span>
                      <span className="text-xs text-tertiary">{when(checkpoint.created_at)}</span>
                    </span>
                    {carries ? (
                      <RawToggle
                        label="recorded data"
                        value={{
                          inputs: checkpoint.inputs,
                          outputs: checkpoint.outputs,
                          output_references: checkpoint.output_references ?? {},
                        }}
                      />
                    ) : null}
                  </li>
                );
              })}
            </ol>
          ) : (
            <span className="text-sm text-quaternary">This run wrote no checkpoints.</span>
          )}
          <div className="flex flex-col gap-1">
            <h4 className="text-xs font-semibold tracking-wide text-tertiary uppercase">
              Log lines not tied to a step
            </h4>
            {reading.logsCapped ? <LogCapNote /> : null}
            {reading.unmatched.length ? (
              <ul className="m-0 flex list-none flex-col divide-y divide-secondary p-0">
                {reading.unmatched.map((entry, index) => (
                  <LogLine key={`${entry.timestamp ?? index}-${index}`} entry={entry} />
                ))}
              </ul>
            ) : (
              <span className="text-sm text-quaternary">
                {reading.logsCapped
                  ? 'Every kept log line belongs to a step above.'
                  : 'Every log line belongs to a step above.'}
              </span>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
